import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  UserPlus, 
  Euro, 
  Clock, 
  Star, 
  TrendingUp, 
  Shield,
  Calendar,
  Briefcase,
  ArrowLeft,
  CheckCircle,
  Users
} from 'lucide-react';

const PartnerHero = ({ onJoinProgram, onBack }) => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      
      {/* Back Button */}
      <div className="container mx-auto px-6 pt-8">
        <Button 
          variant="ghost" 
          onClick={onBack}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('partner.hero.back', 'Kthehu në Shtëpi')}
        </Button>
      </div>

      {/* Hero Section */}
      <div className="container mx-auto px-6 py-12">
        <div className="text-center mb-16">
          <div className="bg-emerald-100 rounded-full p-6 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
            <Users className="h-10 w-10 text-emerald-600" />
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            {t('partner.hero.title', 'Bëhuni Partner i Brillanz & Glanz')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            {t('partner.hero.subtitle', 'Bashkohuni me ekipin tonë profesional dhe fitoni duke punuar në mënyrë fleksibile si pastrues profesional.')}
          </p>
        </div>

        {/* How It Works */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            {t('partner.hero.howItWorks', 'Si Funksionon?')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-emerald-100 rounded-full p-6 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl font-bold text-emerald-600">1</span>
              </div>
              <h3 className="font-semibold mb-2">{t('partner.hero.step1.title', 'Regjistrohuni')}</h3>
              <p className="text-sm text-gray-600">{t('partner.hero.step1.desc', 'Plotësoni formularin e regjistrimit dhe na tregoni për ekspertizën tuaj.')}</p>
            </div>
            
            <div className="text-center">
              <div className="bg-emerald-100 rounded-full p-6 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl font-bold text-emerald-600">2</span>
              </div>
              <h3 className="font-semibold mb-2">{t('partner.hero.step2.title', 'Verifikimi')}</h3>
              <p className="text-sm text-gray-600">{t('partner.hero.step2.desc', 'Ekipi ynë do t\'ju kontaktojë për intervistë dhe verifikim të kualifikimeve.')}</p>
            </div>
            
            <div className="text-center">
              <div className="bg-emerald-100 rounded-full p-6 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl font-bold text-emerald-600">3</span>
              </div>
              <h3 className="font-semibold mb-2">{t('partner.hero.step3.title', 'Merrni Punë')}</h3>
              <p className="text-sm text-gray-600">{t('partner.hero.step3.desc', 'Pranoni punë përmes aplikacionit tonë sipas orarit tuaj të preferuar.')}</p>
            </div>
            
            <div className="text-center">
              <div className="bg-emerald-100 rounded-full p-6 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl font-bold text-emerald-600">4</span>
              </div>
              <h3 className="font-semibold mb-2">{t('partner.hero.step4.title', 'Fitoni Para')}</h3>
              <p className="text-sm text-gray-600">{t('partner.hero.step4.desc', 'Pagesa automatike pas përfundimit të çdo pune përmes Stripe.')}</p>
            </div>
          </div>
        </div>

        {/* Requirements */}
        <div className="max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
            {t('partner.hero.requirements', 'Kërkesa për Partner')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="shadow-md">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  {t('partner.hero.basicReq', 'Kërkesat Bazë')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• {t('partner.hero.req1', 'Moshë mbi 18 vjeç')}</li>
                  <li>• {t('partner.hero.req2', 'Përvojë në pastrimin profesional (të preferuar)')}</li>
                  <li>• {t('partner.hero.req3', 'Dokumenta të vlefshme identifikimi')}</li>
                  <li>• {t('partner.hero.req4', 'Llogari bankare për pagesa')}</li>
                  <li>• {t('partner.hero.req5', 'Telefon smart për aplikacionin')}</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="shadow-md">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Star className="h-5 w-5 text-yellow-500" />
                  {t('partner.hero.benefits', 'Përfitime Shtesë')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• {t('partner.hero.benefit1', 'Transport i vetë (më shumë punë)')}</li>
                  <li>• {t('partner.hero.benefit2', 'Pajisje pastrimi (bonus për punë)')}</li>
                  <li>• {t('partner.hero.benefit3', 'Fleksibilitet në orare')}</li>
                  <li>• {t('partner.hero.benefit4', 'Eksperiencë me produkte ekologjike')}</li>
                  <li>• {t('partner.hero.benefit5', 'Gjuhë të huaja (Gjermane, Angleze)')}</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-12 text-white mb-8">
          <h2 className="text-4xl font-bold mb-4">
            {t('partner.hero.cta', 'Gati të Filloni?')}
          </h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
            {t('partner.hero.ctaDesc', 'Bashkohuni me ekipin tonë profesional dhe filloni të fitoni duke punuar në mënyrë fleksibile!')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={onJoinProgram}
              className="bg-white text-emerald-600 hover:bg-gray-100 font-bold py-4 px-8 text-xl rounded-full"
            >
              <UserPlus className="mr-2 h-6 w-6" />
              {t('partner.hero.joinBtn', 'Bëhu Partner Tani')}
            </Button>
            
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => window.open(`tel:${t('contact.phone', '+41 76 766 94 30')}`, '_self')}
              className="border-white text-white hover:bg-white/10 py-4 px-8 text-xl rounded-full"
            >
              <Briefcase className="mr-2 h-6 w-6" />
              {t('partner.hero.contact', 'Kontaktoni')}: {t('contact.phone', '+41 76 766 94 30')}
            </Button>
          </div>
        </div>

        {/* FAQ Preview */}
        <div className="text-center">
          <p className="text-gray-600 mb-4">
            {t('partner.hero.questions', 'Keni pyetje? Kontaktoni me ne në')}{' '}
            <a href={`mailto:${t('contact.email', 'thaqi.kujtim@gmx.net')}`} className="text-emerald-600 hover:underline font-semibold">
              {t('contact.email', 'thaqi.kujtim@gmx.net')}
            </a>
          </p>
          <p className="text-sm text-gray-500">
            {t('partner.hero.phoneInfo', 'Ose telefononi në')} {t('contact.phone', '+41 76 766 94 30')} {t('partner.hero.moreInfo', 'për më shumë informacione')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PartnerHero;